// DEPENDENCIES
import { useState, useEffect, useRef } from "react";

// TYPES
import { Step } from "../../types";

// COMPONENTS
import { Alert } from "@allaria-tech/matter/Alert";
import { InputSelect } from "@allaria-tech/matter/InputSelect";
import { Checkbox } from "@allaria-tech/matter/Checkbox";
import { ButtonGroup } from "@allaria-tech/matter/ButtonGroup";
import { IconButton } from "@allaria-tech/matter/IconButton";
import { Icon } from "@allaria-tech/matter/Icon";
import { Subtext } from "@allaria-tech/matter/Subtext";
import { TextField } from "@allaria-tech/matter/TextField";
import DniValidationErrorModal from "../DniValidationErrorModal/DniValidationErrorModal";

// UTILS
import { getQuestions } from "../../app/utils";
import { useAppDispatch } from "../../app/hooks";
import { openModal } from "../../reducers/modalReducer";
import { getUnificationParams, pepOptions, withPhonePlus } from "../../utils";
import { ToastMessage } from "@allaria-tech/matter/ToastMessage";
import {
  CRYPTO_TERMS_URL,
  openExternalInApp,
  postToParent,
} from "../../utils/crypto";
import {
  completeCryptoOnboarding,
  createCryptoOnboarding,
} from "../../connectors/connectors";
import useForm from "../../hooks/useForm";
import useRemoteParams from "../../hooks/useRemoteParams";
import useMobileLayout from "../../hooks/useMobileLayout";
import * as Sentry from "@sentry/react";

// STYLES
import styles from "./CryptoAccountStep.module.scss";

const COMPLETION_ERROR_MODAL = "modal-completion-error";

type Props = {
  dispatch: Parameters<NonNullable<Step["component"]>>[0];
  state: Parameters<NonNullable<Step["component"]>>[1];
  formik: Parameters<NonNullable<Step["component"]>>[2];
};

const CryptoAccountStep = ({ formik }: Props) => {
  const { accountName, accountId } = getUnificationParams();
  const {
    values: { token },
  } = useRemoteParams();

  // Volver goes to the phone step, the one before it inside the onboarding.
  const { handleBack } = useForm(formik);

  const { isMobile } = useMobileLayout();

  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState(false);
  // The account data last accepted by POST /crypto/onboarding. A retry after
  // the complete failed sends it again only if something changed since.
  const sentOnboardingData = useRef<string | null>(null);
  const [cryptoExperienceOptions, setCryptoExperienceOptions] = useState<
    { value: string; label: string }[]
  >([]);

  useEffect(() => {
    if (!token) return;

    const fetchQuestions = async () => {
      try {
        const questions = await getQuestions();

        const formattedQuestions =
          questions?.[0]?.choices.map(({ externalCode, option }) => ({
            value: externalCode,
            label: option,
          })) ?? [];

        setCryptoExperienceOptions(formattedQuestions);
      } catch {
        setCryptoExperienceOptions([]);
      }
    };

    fetchQuestions();
  }, [token]);

  const pep = formik?.values?.pepExposed ?? "";
  const pepDetails = formik?.values?.pepDetails ?? "";
  const requiresPepDetails = pep === "DIRECT" || pep === "FAMILY";
  const cryptoExperience = formik?.values?.cryptoExperience ?? "";
  const acceptedTnC = formik?.values?.acceptedCryptoTnC ?? false;

  const canContinue =
    !!pep &&
    (!requiresPepDetails || !!pepDetails.trim()) &&
    !!cryptoExperience &&
    acceptedTnC &&
    !!accountName &&
    !!accountId &&
    !!token;

  // The end of the onboarding: the account data, then the complete against
  // the draft whose identity was validated. The app completes it again to show
  // the account's state, so only a failure stays here.
  const onClickContinue = async () => {
    setIsLoading(true);
    try {
      const onboardingData = {
        accountId: Number(accountId),
        pep,
        pepDetails: pep === "NONE" ? undefined : pepDetails,
        cryptoExperience,
        phone: withPhonePlus(formik?.values?.phone),
        termsAccepted: acceptedTnC,
      };
      const serializedData = JSON.stringify(onboardingData);

      if (sentOnboardingData.current !== serializedData) {
        await createCryptoOnboarding(onboardingData);
        sentOnboardingData.current = serializedData;
      }

      await completeCryptoOnboarding({ accountId: Number(accountId) });

      postToParent("DIGITAL_ONBOARDING_COMPLETED");
    } catch (error) {
      Sentry.captureException(error);
      dispatch(openModal({ name: COMPLETION_ERROR_MODAL }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => postToParent("DIGITAL_ONBOARDING_CLOSE");

  // Opening the PDF is delegated to the app on every platform, because the
  // browser cannot do it from in here: the parent embeds this screen in a
  // sandboxed iframe, the popup target="_blank" opens inherits that sandbox, and
  // Chrome refuses to run its PDF viewer in a sandboxed context — so the tab
  // lands on an error page. Native is worse still: target="_blank" is a silent
  // no-op in the WebView. The app's openExternal runs outside the sandbox.
  const handleTermsClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // The anchor sits inside the checkbox label, so the tap would toggle the
    // acceptance on its way up.
    e.stopPropagation();

    // The href is left in place as the fallback for a build with no parent to
    // ask, which is also what keeps the link's semantics.
    if (openExternalInApp(CRYPTO_TERMS_URL)) e.preventDefault();
  };

  const termsCheckbox = (
    <Checkbox
      checked={acceptedTnC}
      onCheckedChange={(checked) =>
        formik?.setFieldValue("acceptedCryptoTnC", checked === true)
      }
      label={
        <>
          <span className={styles.checkboxText}>Acepto los </span>
          <a
            className={styles.checkboxLink}
            href={CRYPTO_TERMS_URL}
            target="_blank"
            rel="noreferrer"
            onClick={handleTermsClick}
          >
            Términos y condiciones
          </a>
        </>
      }
    />
  );

  return (
    <section className={`${styles.container} ${isMobile ? styles.phone : ""}`}>
      <ToastMessage messageId="formAlert" position="TOP" />
      <DniValidationErrorModal
        title="No pudimos terminar la apertura"
        description="Validamos tu identidad, pero no pudimos finalizar la apertura de tu cuenta. Por favor, intentá nuevamente."
        modalName={COMPLETION_ERROR_MODAL}
        onRetry={onClickContinue}
        onClose={handleClose}
      />
      <div className={styles.titleRow}>
        <h2 className={styles.title}>Crear cuenta a Allaria Digital</h2>
        {!isMobile && (
          <IconButton
            variant="ghost"
            aria-label="Cerrar"
            icon={<Icon name="close" />}
            onClick={handleClose}
          />
        )}
      </div>
      <p className={styles.subtitle}>
        Es necesario aceptar los Términos y condiciones para poder utilizar esta
        funcionalidad.
      </p>

      <dl className={styles.accountInfo}>
        <dt className={styles.accountLabel}>Cuenta Comitente</dt>
        <dd className={styles.accountName}>
          {accountName || "Sin denominación"}
        </dd>
        <dd className={styles.accountId}>
          {accountId || "Sin número de comitente"}
        </dd>
      </dl>

      <div className={styles.fields}>
        <div>
          <InputSelect
            label="¿Sos expuesto políticamente?"
            options={pepOptions}
            value={pep}
            placeholder="Seleccioná una opción"
            disableBottomSheet={!isMobile}
            labelAction={{
              text: "¿Qué significa?",
              onClick: () => dispatch(openModal({ name: "modal-PEP" })),
            }}
            onChange={(value) => formik?.setFieldValue("pepExposed", value)}
          />
          {/* InputSelect's message is always gray; the design wants info blue. */}
          <Subtext variant="info" message="Res 35/2023 y 192/2024 UIF" />
        </div>
        {requiresPepDetails && (
          <div>
            <TextField
              name="pepDetails"
              label="Describí brevemente la situación"
              value={pepDetails}
              // Same limit as largeStringRequired.
              maxLength={200}
              onChange={(value) => formik?.setFieldValue("pepDetails", value)}
            />
          </div>
        )}
        <div>
          <InputSelect
            label="¿Cuál es su experiencia previa en Activos Virtuales?"
            options={cryptoExperienceOptions}
            value={cryptoExperience}
            placeholder="Seleccioná una opción"
            disableBottomSheet={!isMobile}
            onChange={(value) =>
              formik?.setFieldValue("cryptoExperience", value)
            }
          />
        </div>
      </div>

      <Alert variant="WARN" className={styles.alert}>
        Con el fin de optimizar la operatoria desde tu comitente hacia Allaria
        Digital, las transacciones implican la compra y/o venta de títulos
        públicos en tu cuenta comitente de Allaria. En consecuencia, esta
        operatoria podría limitar el acceso al Mercado Libre de Cambios (MLC).
      </Alert>

      {!isMobile && <div className={styles.checkboxRow}>{termsCheckbox}</div>}

      <ButtonGroup
        container={isMobile}
        containerClassName={styles.buttons}
        align="center"
        disableMobileLayout={!isMobile}
        secondary={{ text: "Volver", type: "button", onClick: handleBack }}
        primary={{
          text: "Continuar",
          type: "button",
          isDisabled: !canContinue || isLoading,
          isLoading,
          onClick: onClickContinue,
        }}
      >
        {isMobile && <div className={styles.phoneTerms}>{termsCheckbox}</div>}
      </ButtonGroup>
    </section>
  );
};

export default CryptoAccountStep;
